import { useContext, useEffect, useState } from "react";
import { StepNextButton } from "../../../components";
import { StepperContext, useLoanApplication } from "../../../contexts";
import {
  LoanApplicationStage,
  LoanProductType,
  ShareholderKYCApplicationStatus,
} from "../../../hooks/api/types";
import {
  InvoiceReviewSection,
  BusinessInformationReviewSection,
  ConnectedBanksReviewSection,
  DocumentsReviewSection,
  LoanInformationReviewSection,
  OwnersInformationReviewSection,
} from "./sections";
import { useIFrameEvents, useLoanMetrics } from "../../../hooks";
import { SumSubKycModal } from "../OwnersInformation";
import ShareHolderDialogModal from "./ShareHolderDialogModal";
import { useLoanProductConfigFlags } from "../../../hooks/useLoanProductConfigFlags";
import { MerchantIdInformationSection } from "./MerchantIdInformationSection";
import VendorReviewSection from "./sections/VendorReviewSection";

type Props = {};

const KYC_PROGRESSED_STATUSES = [
  ShareholderKYCApplicationStatus.IN_PROGRESS,
  ShareholderKYCApplicationStatus.COMPLETED,
];

const ApplicationReview = (props: Props) => {
  const { nextStep } = useContext(StepperContext);
  const {
    actions: { update, refetch },
    state,
  } = useLoanApplication();
  const [kycModalOpen, setKycModalOpen] = useState(false);
  const { postLoanApplicationCompletedEvent } = useIFrameEvents();
  const [shareHolderMessageModalOpen, setShareHolderMessageModalOpen] =
    useState(false);
  const { loanAmount } = useLoanMetrics();

  const {
    disableBankStatementsUpload,
    disableInvoiceUpload,
    showMerchantIdStep,
    enableVendorScreen,
  } = useLoanProductConfigFlags(state.loanType, loanAmount);

  const applicantNeedsKyc = Boolean(
    state.applicant?.hasPOA || state.applicant?.isStakeholder
  );
  const kycStatus = state.applicant?.latestKycApplication?.status;
  const kycProgressed = Boolean(
    kycStatus && KYC_PROGRESSED_STATUSES.includes(kycStatus)
  );
  // PENDING (e.g. copy reuse failed) or never started — block submit until
  // Sumsub has at least been submitted (IN_PROGRESS) or completed.
  const mustVerifyBeforeSubmit = applicantNeedsKyc && !kycProgressed;
  const isKycRed =
    state.applicant?.latestKycApplication?.kycApplication?.reviewAnswer ===
    "RED";

  useEffect(() => {
    refetch();
  }, []);

  const hasPoaHolder =
    !!state?.individualShareholders?.some((s) => s.hasPOA) ||
    !!state?.companyShareholders?.some((s) => s.hasPOA);

  const submitApplication = async () => {
    await update({
      ...state,
      stage: LoanApplicationStage.SUCCESS,
    });
    nextStep();
    postLoanApplicationCompletedEvent({
      loanApplicationId: state.id,
    });
  };

  const handleComplete = async () => {
    if (!hasPoaHolder) {
      setShareHolderMessageModalOpen(true);
      return;
    }

    if (mustVerifyBeforeSubmit) {
      return;
    }

    try {
      await submitApplication();
    } catch (err) {
      console.log(err);
    }
  };

  // Persist IN_PROGRESS only when KYC is not already progressed/completed,
  // then return to review — user must click Complete themselves.
  const handleKycModalSubmit = async () => {
    try {
      const currentStatus = state.applicant?.latestKycApplication?.status;
      if (
        currentStatus !== ShareholderKYCApplicationStatus.COMPLETED &&
        currentStatus !== ShareholderKYCApplicationStatus.IN_PROGRESS
      ) {
        await update({
          applicant: {
            ...state.applicant,
            // Explicit status object for API (not the hydrated kycApplication array).
            kycApplication: {
              status: ShareholderKYCApplicationStatus.IN_PROGRESS,
            } as (typeof state.applicant)["kycApplication"],
            latestKycApplication: {
              ...state.applicant?.latestKycApplication,
              status: ShareholderKYCApplicationStatus.IN_PROGRESS,
            },
          },
        });
      }
      await refetch();
    } catch (err) {
      console.log(err);
    } finally {
      setKycModalOpen(false);
    }
  };

  return (
    <div className="cx-max-w-[960px] cx-w-full cx-flex cx-flex-col cx-items-center cx-px-1 sm:cx-px-2">
      <div className="cx-text-3xl cx-font-bold cx-text-center">
        Review your application
      </div>
      <div className="cx-text-text-secondary cx-text-base cx-text-center cx-mt-2">
        Carefully review all of your details before submitting the application
      </div>
      <div className="cx-w-full cx-flex cx-flex-col cx-items-stretch cx-min-w-0">
        <LoanInformationReviewSection />
        {(state.loanType === LoanProductType.INVOICE_DISCOUNTING ||
          state.loanType === LoanProductType.PAYABLE_FINANCING) &&
          !disableInvoiceUpload && <InvoiceReviewSection />}
        <BusinessInformationReviewSection />
        <OwnersInformationReviewSection />
        {showMerchantIdStep && <MerchantIdInformationSection />}
        {!disableBankStatementsUpload && <ConnectedBanksReviewSection />}
        {enableVendorScreen && <VendorReviewSection />}
        <DocumentsReviewSection />
      </div>
      <div className="cx-w-full cx-flex cx-flex-col cx-items-center cx-gap-4 cx-max-w-[400px] cx-mt-10 cx-mb-10">
        {mustVerifyBeforeSubmit && (
          <>
            <p className="cx-text-sm cx-text-text-secondary cx-text-center cx-px-2">
              Please complete identity verification before submitting your
              application.
            </p>
            <StepNextButton
              arrow={"right"}
              label={"Start verification\u00A0\u00A0"}
              loading={false}
              onClick={() => setKycModalOpen(true)}
            />
          </>
        )}
        <StepNextButton
          arrow={"right"}
          label={"Complete\u00A0\u00A0"}
          loading={state.updating}
          disabled={mustVerifyBeforeSubmit}
          onClick={handleComplete}
        />
        {!mustVerifyBeforeSubmit && isKycRed && applicantNeedsKyc && (
          <StepNextButton
            arrow={"right"}
            label={"Retry Verification\u00A0\u00A0"}
            loading={false}
            onClick={() => setKycModalOpen(true)}
          />
        )}
      </div>
      <SumSubKycModal
        isOpen={kycModalOpen}
        onClose={() => setKycModalOpen(false)}
        onPrev={() => setKycModalOpen(false)}
        onSubmit={handleKycModalSubmit}
      />
      <ShareHolderDialogModal
        isOpen={shareHolderMessageModalOpen}
        onClose={() => setShareHolderMessageModalOpen(false)}
      />
    </div>
  );
};

export default ApplicationReview;
